// src/screens/crew/CrewManageScreen.js
// 74차: 신규 생성 — 크루장/관리자 전용 관리 화면.
// 크루장 전용: 이름 변경, 관리자 지정/해제, 해체
// 크루장+관리자 공통: 가입 승인/거절, 강퇴
// 탈퇴: 관리자/일반 멤버는 바로 가능(7일 잠금 적용), 크루장은 관리자가 있어야만 승계 후 가능
// 75차: "크루 정보를 불러올 수 없습니다"만 뜨던 버그 수정 — 실제 원인은 크루 문서 자체가
// 아니라 그 다음 멤버 프로필 조회(getCrewMemberProfiles, users 컬렉션 쿼리)가
// firestore.rules상 list:isAdmin()에 막혀 permission-denied였던 것. Admin SDK로 크루장/
// 관리자 여부를 검증하는 새 CF(getCrewMemberProfilesCF)로 이관해 해결. 자체 커스텀 헤더
// (뒤로가기)도 공통 AppHeader로 교체 — 특히 에러 분기(!crew)엔 아예 헤더 자체가 없어서
// 이 문구만 뜨면 뒤로 갈 방법이 없었음.
// 77차: loading/에러 분기의 AppHeader에 제목 누락("‹ 뒤로"만 보이던 것) 복원 — 메인
// 렌더(하단)는 이미 centerElement가 있었지만 이 두 분기는 빠져있었음.
// 78차: 승인/거절/관리자지정/강퇴 등 작은 인라인 버튼과 탈퇴/해체 버튼을 공용
// SecondaryButton/DangerButton으로 교체(작은 버튼은 style로 패딩만 축소)
import React, { useState, useCallback, useRef } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, ActivityIndicator,
} from 'react-native';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { LinearGradient } from 'expo-linear-gradient';
// 79차: crews/ Storage 경로가 로그인만 하면 누구나 직접 write/delete할 수 있어서
// (crews.imageUrl이 공개 read라 현재 파일 경로가 노출됨) 다른 크루의 대표사진을
// 임의로 덮어쓰거나 지울 수 있던 취약점 — themes/notices(78차50차)와 동일하게
// uploadCrewImageCF/deleteCrewImageCF(크루장/관리자 서버측 검증) 경유로 전환.
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useFocusEffect } from '@react-navigation/native';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { SecondaryButton, DangerButton } from '../../components/common/Buttons';
import { COLORS, GRADIENTS } from '../../constants/theme';
import {
  getCrewById, getCrewMemberProfilesCF, renameCrew, updateCrewImage, addCrewNotice,
  approveCrewMember, rejectCrewMember, kickCrewMember,
  appointManager, dismissManager, leaveCrew, leaderLeaveCrew, disbandCrew, expandCrewCap,
  CREW_RENAME_COST, CREW_DISBAND_MAX_MEMBERS, getCrewCapUpgrade, getCrewCap,
} from '../../services/crewService';
import { PeopleIcon, MenuBellIcon, MenuNicknameIcon, CameraIcon, CrownIcon, AdminIcon, IconLabel } from '../../components/common/Icons';

const GOLD       = COLORS.gold;
const BG         = COLORS.bg;
const SURFACE    = COLORS.surface;
const SURFACE2   = COLORS.surface2;
const BORDER     = COLORS.border;
const TEXT       = COLORS.text;
const TEXT_SEC   = COLORS.textSec;
const TEXT_MUTED = COLORS.textMuted;
const ON_GOLD    = COLORS.onGold;
const RED        = COLORS.red;

// 78차 39차: 크루 관리 화면이 순수 텍스트 리스트뿐이라 "너무 비어보인다"는 피드백 —
// 멤버 행마다 닉네임 이니셜 아바타를 추가해 시각적 무게감을 줌(새 이미지 자산 없이
// 색상 원+글자만으로 가벼운 개선).
function MemberAvatar({ nickname, isLeader, isManager }) {
  const initial = (nickname || '?').trim().charAt(0).toUpperCase() || '?';
  const bg = isLeader ? COLORS.goldBg : isManager ? COLORS.blue + '22' : SURFACE2;
  const color = isLeader ? GOLD : isManager ? COLORS.blue : TEXT_MUTED;
  return (
    <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: bg, borderWidth: 1, borderColor: color + '55', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
      <Text style={{ color, fontSize: 14, fontWeight: '800' }}>{initial}</Text>
    </View>
  );
}

// 80차 계속: [버그 수정] crewService.js/usePointsFIFO_CF가 던지는 영문 에러코드가 그대로
// showAlert에 노출되고 있었음(예: "INSUFFICIENT_POINTS") — withBusy 한 곳에서 전부
// 한글로 변환(이미 한글인 메시지는 맵에 없어 그대로 통과).
const ERROR_MESSAGES = {
  NOT_FOUND: '크루 정보를 찾을 수 없어요.',
  NOT_LEADER: '크루장만 할 수 있어요.',
  NAME_TOO_SHORT: '크루명은 2자 이상이어야 해요.',
  NAME_TOO_LONG: '크루명은 16자 이하여야 해요.',
  ALREADY_IN_CREW: '이미 다른 크루에 가입돼 있어요.',
  ALREADY_MAX: '이미 최대 정원이에요.',
  CAP_CHANGED: '정원 정보가 바뀌었어요. 다시 시도해주세요.',
  CREW_FULL: '크루 정원이 가득 찼어요.',
  CANNOT_KICK_LEADER: '크루장은 강퇴할 수 없어요.',
  LEADER_MUST_USE_LEADER_LEAVE: '크루장은 "크루장 탈퇴"를 이용해주세요.',
  ACTION_LOCKED: '아직은 이 작업을 할 수 없어요.',
  NEED_MANAGER: '먼저 관리자를 지정해야 해요.',
  TOO_MANY_MEMBERS: '멤버가 5명 이하일 때만 해체할 수 있어요.',
  ALREADY_REPORTED: '이미 신고했어요.',
  INSUFFICIENT_POINTS: '포인트가 부족해요.',
};

export default function CrewManageScreen({ navigation, route }) {
  const { user, profile, refreshProfile } = useAuth();
  const crewId = route.params?.crewId;
  const [crew, setCrew] = useState(null);
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renameText, setRenameText] = useState('');
  const [busy, setBusy] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeText, setNoticeText] = useState('');
  const [noticeSending, setNoticeSending] = useState(false);
  // 79차: 화면을 빠르게 나갔다 다시 들어오면 이전 포커스의 load()가 늦게 응답해 최신
  // 상태를 오래된 것으로 덮어쓸 수 있었음 — activeRef로 이전 포커스 세션의 setState만 무시.
  const activeRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const c = await getCrewById(crewId);
      if (!activeRef.current) return;
      if (!c) { setCrew(null); return; }
      setCrew(c);
      setRenameText(c.name);
      const { members: memberProfiles, pending: pendingProfiles } = await getCrewMemberProfilesCF(crewId);
      if (!activeRef.current) return;
      setMembers(memberProfiles);
      setPending(pendingProfiles);
    } catch { if (activeRef.current) setCrew(null); }
    finally { if (activeRef.current) setLoading(false); }
  }, [crewId]);

  useFocusEffect(useCallback(() => {
    activeRef.current = true;
    setLoading(true);
    load();
    return () => { activeRef.current = false; };
  }, [load]));

  if (loading) return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>크루 관리</Text>} />
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator color={GOLD} />
      </View>
    </View>
  );
  if (!crew) return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>크루 관리</Text>} />
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ color: TEXT_MUTED }}>크루 정보를 불러올 수 없습니다.</Text>
      </View>
    </View>
  );

  const isLeader  = crew.ownerUid === user.uid;
  const isManager = crew.managerUid === user.uid;
  const isLocked  = new Date(crew.actionLockUntil) > new Date();
  const memberCount = (crew.memberUids || []).length;
  const cap = getCrewCap(crew);
  const nextCapTier = getCrewCapUpgrade(cap);
  const isFullForExpand = memberCount >= cap; // 80차 계속: 가득 찼으면 다음 단계 무료

  const withBusy = (fn) => async (...args) => {
    try { setBusy(true); await fn(...args); await load(); }
    catch (e) { showAlert('오류', ERROR_MESSAGES[e.message] || e.message || '처리에 실패했어요.'); }
    finally { setBusy(false); }
  };

  const handleRename = withBusy(async () => {
    const trimmed = renameText.trim();
    if (trimmed === crew.name) return;
    if ((profile?.points || 0) < CREW_RENAME_COST) throw new Error('포인트가 부족해요.');
    await renameCrew({ crewId, uid: user.uid, newName: trimmed });
    await refreshProfile();
    // 80차 계속: 완료 후 크루 홈탭(CrewScreen의 CrewHome, 이 화면이 push된 자리)으로 이동
    showAlert('완료', '크루명이 변경됐어요.', [{ text: '확인', onPress: () => navigation.goBack() }]);
  });

  const handleExpandCap = withBusy(async () => {
    if (!nextCapTier) return;
    if (!isFullForExpand && (profile?.points || 0) < nextCapTier.cost) throw new Error('포인트가 부족해요.');
    const result = await expandCrewCap({ crewId, uid: user.uid });
    await refreshProfile();
    showAlert('완료', result.cost === 0
      ? `정원을 꽉 채워주셔서 무료로 ${result.to}명까지 늘었어요! 🎉`
      : `정원이 ${result.to}명으로 늘었어요.`);
  });

  // 79차: 압축까지는 그대로, 실제 Storage 쓰기는 uploadCrewImageCF(Admin SDK, 크루장/
  // 관리자 검증)로 이관 — SuperAdminStoresScreen.js의 blobToBase64+uploadAdminImageCF와
  // 동일한 패턴(new FileReader로 base64 인코딩해 CF에 전달).
  const handlePickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') { showAlert('알림', '사진 접근 권한이 필요해요.'); return; }
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.8 });
    if (result.canceled) return;
    setBusy(true);
    try {
      const compressed = await ImageManipulator.manipulateAsync(
        result.assets[0].uri, [{ resize: { width: 300 } }],
        { compress: 0.85, format: ImageManipulator.SaveFormat.JPEG }
      );
      const blob = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = () => resolve(xhr.response);
        xhr.onerror = () => reject(new Error('이미지 로드 실패'));
        xhr.responseType = 'blob';
        xhr.open('GET', compressed.uri, true);
        xhr.send(null);
      });
      const imageBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('이미지 인코딩 실패'));
        reader.readAsDataURL(blob);
      });
      blob.close && blob.close();
      const uploadCF = httpsCallable(functions, 'uploadCrewImageCF');
      const { data } = await uploadCF({ crewId, imageBase64, contentType: 'image/jpeg' });
      const prevUrl = crew.imageUrl;
      await updateCrewImage(crewId, data.url);
      // 78차 71차: 새 대표사진으로 교체되면 이전 파일은 고아 파일이 되므로 정리
      if (prevUrl) {
        const match = prevUrl.match(/crews%2F([^?]+)/);
        if (match) {
          const deleteCF = httpsCallable(functions, 'deleteCrewImageCF');
          deleteCF({ crewId, fileName: decodeURIComponent(match[1]) }).catch(() => {});
        }
      }
      await load();
    } catch (e) {
      showAlert('오류', '사진 업로드에 실패했어요.');
    } finally { setBusy(false); }
  };

  // 78차 66차: 크루장/관리자 전용 공지사항 — crewPosts에 type:'notice'로 저장되어
  // 크루 게시판(CrewScreen)에 자동으로 노출됨(별도 조회 화면 불필요).
  const handleSendNotice = async () => {
    const title = noticeTitle.trim();
    const text = noticeText.trim();
    if (!text) { showAlert('알림', '공지 내용을 입력해주세요.'); return; }
    setNoticeSending(true);
    try {
      await addCrewNotice({ crewId, uid: user.uid, nickname: profile?.nickname || crew.ownerNickname || '크루장', title, text });
      setNoticeTitle(''); setNoticeText('');
      showAlert('완료', '공지가 등록됐어요. 크루 게시판에서 확인할 수 있어요.', [{ text: '확인', onPress: () => navigation.goBack() }]);
    } catch (e) {
      showAlert('오류', '공지 등록에 실패했어요.');
    } finally { setNoticeSending(false); }
  };

  const handleApprove   = withBusy((uid) => approveCrewMember(crewId, uid));
  const handleReject    = withBusy((uid) => rejectCrewMember(crewId, uid));
  const handleKick      = (uid, nickname) => showAlert('강퇴', `${nickname}님을 강퇴하시겠어요?`, [
    { text: '취소', style: 'cancel' },
    { text: '강퇴', style: 'destructive', onPress: withBusy(() => kickCrewMember(crewId, uid)) },
  ]);
  const handleAppoint    = (uid) => withBusy(() => appointManager(crewId, uid))();

  const handleLeave = () => {
    if (isLocked) { showAlert('알림', '가입(생성) 후 7일 동안은 탈퇴할 수 없어요.'); return; }
    showAlert('크루 탈퇴', '정말 탈퇴하시겠어요?', [
      { text: '취소', style: 'cancel' },
      { text: '탈퇴', style: 'destructive', onPress: withBusy(async () => {
        await leaveCrew(crewId, user.uid);
        await refreshProfile();
        navigation.goBack();
      }) },
    ]);
  };

  const handleLeaderLeave = () => {
    if (isLocked) { showAlert('알림', '가입(생성) 후 7일 동안은 탈퇴할 수 없어요.'); return; }
    if (!crew.managerUid) { showAlert('관리자 필요', '먼저 관리자를 지정해야 크루장 탈퇴가 가능해요.'); return; }
    showAlert('크루장 탈퇴', '관리자에게 크루장을 넘기고 탈퇴하시겠어요?', [
      { text: '취소', style: 'cancel' },
      { text: '탈퇴', style: 'destructive', onPress: withBusy(async () => {
        await leaderLeaveCrew(crewId, user.uid);
        await refreshProfile();
        navigation.goBack();
      }) },
    ]);
  };

  const handleDisband = () => {
    if (isLocked) { showAlert('알림', '생성 후 7일 동안은 해체할 수 없어요.'); return; }
    if (memberCount > CREW_DISBAND_MAX_MEMBERS) { showAlert('알림', `총원이 ${CREW_DISBAND_MAX_MEMBERS}명 이하일 때만 해체할 수 있어요.`); return; }
    showAlert('크루 해체', '해체하면 되돌릴 수 없고, 사용한 포인트도 돌려받지 못해요. 계속할까요?', [
      { text: '취소', style: 'cancel' },
      { text: '해체', style: 'destructive', onPress: withBusy(async () => {
        await disbandCrew(crewId, user.uid);
        await refreshProfile();
        // 79차: [버그 수정] 'Crew'는 GeneralTabNavigator 안에 nested된 탭 이름이라
        // top-level AppStack 화면인 이 화면(CrewManage)에서 bare navigate로는 못 찾음 —
        // 해체 직후 아무 반응도 없이 이미 사라진 크루의 관리 화면에 그대로 머물렀을 것.
        // 바로 위 handleLeaderLeave(크루장 탈퇴)가 이미 쓰고 있는 goBack()으로 통일
        // (같은 화면에서 형제 핸들러 중 하나만 다른 방식을 쓰던 불일치, 전수 정독으로 발견).
        navigation.goBack();
      }) },
    ]);
  };

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>크루 관리</Text>} />

      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 48 }}>
        {/* 78차 69차: 가독성 개선 — 섹션마다 카드로 분리해 경계를 명확히 하고, 사진 변경은
            "사진 변경" 버튼 대신 개인 프로필 사진과 동일하게 아바타 자체를 눌러 바꾸는
            방식(카메라 배지 오버레이)으로 통일. */}
        <View style={[s.summaryCard, { alignItems: 'center' }]}>
          <TouchableOpacity
            style={s.avatarWrap}
            onPress={(isLeader || isManager) ? handlePickImage : undefined}
            disabled={busy || !(isLeader || isManager)}
            accessibilityLabel="크루 대표 사진 변경">
            <LinearGradient colors={GRADIENTS.button} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={s.avatarRing}>
              {busy ? (
                <View style={s.avatarInner}><ActivityIndicator color={GOLD} /></View>
              ) : crew.imageUrl ? (
                <Image source={{ uri: crew.imageUrl }} style={s.avatarImg} />
              ) : (
                <View style={s.avatarInner}>
                  <Text style={s.avatarTxt}>{(crew.name || '?')[0].toUpperCase()}</Text>
                </View>
              )}
            </LinearGradient>
            {(isLeader || isManager) && (
              <View style={s.cameraBadge}><CameraIcon size={11} color="#0a0a14" /></View>
            )}
          </TouchableOpacity>
          <Text style={[s.summaryName, { marginTop: 10, textAlign: 'center' }]} numberOfLines={1}>{crew.name}</Text>
          <Text style={s.summarySub}>멤버 {memberCount} / {cap}명 · 크루장 {crew.ownerNickname || '알 수 없음'}</Text>
        </View>

        {isLeader && (
          <View style={s.card}>
            <View style={s.sectionTitleRow}>
              <PeopleIcon size={13} color={GOLD} />
              <Text style={s.sectionTitle}>정원 확장</Text>
            </View>
            {nextCapTier ? (
              <>
                <Text style={{ color: TEXT_SEC, fontSize: 12, marginBottom: 10 }}>
                  {isFullForExpand
                    ? `정원이 가득 찼어요! 지금 확장하면 ${nextCapTier.to}명까지 무료예요.`
                    : `현재 정원 ${cap}명 → ${nextCapTier.to}명으로 늘릴 수 있어요 (${nextCapTier.cost}P, 정원을 다 채우면 무료)`}
                </Text>
                <SecondaryButton
                  label={isFullForExpand ? `정원 ${nextCapTier.to}명으로 무료 확장하기` : `정원 ${nextCapTier.to}명으로 늘리기 (${nextCapTier.cost}P)`}
                  onPress={handleExpandCap}
                  disabled={busy}
                />
              </>
            ) : (
              <Text style={{ color: TEXT_MUTED, fontSize: 12 }}>이미 최대 정원({cap}명)이에요.</Text>
            )}
          </View>
        )}

        {isLocked && (
          <View style={s.lockNotice}>
            <Text style={s.lockNoticeText}>생성 후 7일 동안은 이름 변경만 가능하고, 탈퇴·해체는 할 수 없어요.</Text>
          </View>
        )}

        {isLeader && (
          <View style={s.card}>
            <View style={s.sectionTitleRow}>
              <MenuNicknameIcon size={13} color={GOLD} />
              <Text style={s.sectionTitle}>크루명 변경 ({CREW_RENAME_COST}P)</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TextInput style={[s.input, { flex: 1 }]} value={renameText} onChangeText={setRenameText} maxLength={16} />
              <SecondaryButton label="변경" onPress={handleRename} disabled={busy} style={s.smallBtn} textStyle={s.smallBtnText} />
            </View>
          </View>
        )}

        {(isLeader || isManager) && (
          <View style={s.card}>
            <View style={s.sectionTitleRow}>
              <MenuBellIcon size={13} color={GOLD} />
              <Text style={s.sectionTitle}>공지사항 작성</Text>
            </View>
            <View style={{ gap: 8 }}>
              <TextInput style={s.input} value={noticeTitle} onChangeText={setNoticeTitle} placeholder="제목 (선택)" placeholderTextColor={TEXT_MUTED} maxLength={30} />
              <TextInput style={[s.input, s.noticeInput]} value={noticeText} onChangeText={setNoticeText} placeholder="크루원에게 전할 공지사항이나 주요 일정을 적어주세요" placeholderTextColor={TEXT_MUTED} multiline maxLength={300} />
              <GradientButton label={noticeSending ? '등록 중...' : '공지 등록'} onPress={handleSendNotice} disabled={noticeSending} />
            </View>
          </View>
        )}

        {pending.length > 0 && (
          <View style={s.card}>
            <View style={s.sectionTitleRow}>
              <MenuBellIcon size={13} color={GOLD} />
              <Text style={s.sectionTitle}>가입 신청 ({pending.length})</Text>
            </View>
            {pending.map(p => (
              <View key={p.uid} style={s.memberRow}>
                <MemberAvatar nickname={p.nickname} />
                <Text style={s.memberName}>{p.nickname || '(닉네임 없음)'}</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <SecondaryButton label="승인" onPress={() => handleApprove(p.uid)} disabled={busy} style={s.smallBtn} textStyle={s.smallBtnText} />
                  <DangerButton label="거절" onPress={() => handleReject(p.uid)} disabled={busy} style={s.smallBtn} textStyle={s.smallBtnTextSize} />
                </View>
              </View>
            ))}
          </View>
        )}

        <View style={s.card}>
          <View style={s.sectionTitleRow}>
            <PeopleIcon size={13} color={GOLD} />
            <Text style={s.sectionTitle}>멤버 ({members.length})</Text>
          </View>
          {members.map(m => {
            const memberIsLeader  = m.uid === crew.ownerUid;
            const memberIsManager = m.uid === crew.managerUid;
            return (
              <View key={m.uid} style={s.memberRow}>
                <MemberAvatar nickname={m.nickname} isLeader={memberIsLeader} isManager={memberIsManager} />
                <View style={{ flex:1, flexDirection:'row', alignItems:'center', gap:4, flexWrap:'wrap' }}>
                  <Text style={s.memberName}>{m.nickname || '(닉네임 없음)'}</Text>
                  {memberIsLeader && <IconLabel Icon={CrownIcon} size={s.roleTag.fontSize || 11} color={s.roleTag.color} textStyle={s.roleTag}>크루장</IconLabel>}
                  {memberIsManager && <IconLabel Icon={AdminIcon} size={s.roleTag.fontSize || 11} color={s.roleTag.color} textStyle={s.roleTag}>관리자</IconLabel>}
                </View>
                {isLeader && !memberIsLeader && (
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    {memberIsManager ? (
                      <SecondaryButton label="관리자 해제" onPress={() => withBusy(() => dismissManager(crewId))()} disabled={busy} style={s.smallBtn} textStyle={s.smallBtnText} />
                    ) : (
                      <SecondaryButton label="관리자 지정" onPress={() => handleAppoint(m.uid)} disabled={busy} style={s.smallBtn} textStyle={s.smallBtnText} />
                    )}
                    <DangerButton label="강퇴" onPress={() => handleKick(m.uid, m.nickname)} disabled={busy} style={s.smallBtn} textStyle={s.smallBtnTextSize} />
                  </View>
                )}
                {isManager && !memberIsLeader && !memberIsManager && (
                  <DangerButton label="강퇴" onPress={() => handleKick(m.uid, m.nickname)} disabled={busy} style={s.smallBtn} textStyle={s.smallBtnTextSize} />
                )}
              </View>
            );
          })}
        </View>

        <View style={s.dangerZone}>
          <Text style={s.dangerZoneTitle}>⚠ 위험 구역</Text>
          {isLeader ? (
            <>
              <DangerButton label="크루장 탈퇴 (관리자에게 승계)" onPress={handleLeaderLeave} disabled={busy} />
              <DangerButton
                label={`크루 해체 (총원 ${CREW_DISBAND_MAX_MEMBERS}명 이하일 때만)`}
                onPress={handleDisband}
                disabled={busy}
                style={{ marginTop: 10 }}
              />
            </>
          ) : (
            <DangerButton label="크루 탈퇴" onPress={handleLeave} disabled={busy} />
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  headerTitle: { color: TEXT, fontSize: 15, fontWeight: '800' },
  lockNotice: { backgroundColor: COLORS.warningBg, borderRadius: 10, borderWidth: 1, borderColor: COLORS.warning, padding: 12, marginBottom: 16 },
  lockNoticeText: { color: COLORS.warning, fontSize: 12, fontWeight: '600' },
  // 78차 69차: 섹션마다 카드로 분리(가독성 개선 요청) — 공용 카드 스타일
  card: { backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: BORDER, padding: 16, marginBottom: 16 },
  summaryCard: { backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: COLORS.goldBorder, padding: 20, marginBottom: 16 },
  // 78차 69차: "사진 변경" 버튼 대신 개인 프로필 사진과 동일한 아바타+카메라 배지 방식
  avatarWrap: { position: 'relative' },
  avatarRing: { width: 72, height: 72, borderRadius: 36, padding: 2, alignItems: 'center', justifyContent: 'center' },
  avatarInner: { width: '100%', height: '100%', borderRadius: 34, backgroundColor: SURFACE2, alignItems: 'center', justifyContent: 'center' },
  avatarImg: { width: '100%', height: '100%', borderRadius: 34 },
  avatarTxt: { fontSize: 26, fontWeight: '800', color: GOLD },
  cameraBadge: { position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderRadius: 11,
                 backgroundColor: GOLD, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: SURFACE },
  summaryName: { color: TEXT, fontSize: 16, fontWeight: '800' },
  summarySub: { color: TEXT_MUTED, fontSize: 12, marginTop: 3 },
  noticeInput: { minHeight: 70, paddingTop: 12, textAlignVertical: 'top' },
  sectionTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12 },
  sectionTitle: { color: GOLD, fontSize: 13, fontWeight: '800' },
  input: { backgroundColor: SURFACE2, borderRadius: 10, borderWidth: 1, borderColor: BORDER, paddingHorizontal: 14, height: 46, color: TEXT, fontSize: 14 },
  memberRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: BORDER },
  memberName: { color: TEXT, fontSize: 13, fontWeight: '600', flex: 1 },
  roleTag: { color: GOLD, fontSize: 11, fontWeight: '700' },
  smallBtn: { paddingHorizontal: 10, paddingVertical: 6 },
  smallBtnText: { fontSize: 11 },
  smallBtnTextSize: { fontSize: 11 },
  // 78차 69차: 탈퇴/해체(되돌릴 수 없는 액션)를 시각적으로 구분되는 별도 영역으로 분리
  dangerZone: { backgroundColor: COLORS.redBg, borderRadius: 14, borderWidth: 1, borderColor: RED + '40', padding: 16, marginTop: 8 },
  dangerZoneTitle: { color: RED, fontSize: 12, fontWeight: '800', marginBottom: 10 },
});
